import { Tabs, TabsHeader, Tab } from '@material-tailwind/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import HFbutton from '../../../components/button/HFbutton';
import TextWinds from '../../../components/common/TextWinds';
import RegisterStudentDialog from '../../../components/lms/student/popup/RegisterStudentDialog';
import StudentList from '../../../components/lms/student/StudentList';
import {
  STUDENT_STATUS_LABEL,
  StudentStatusEnum,
  studentType,
} from '../../../constants/types/student-type';
import { useParams, useSearchParams } from 'react-router-dom';
import { PaginationProps } from '../../../constants/types';
import {
  getStudentList,
  getStudentTransferRequestList,
  StudentTransferResult,
} from '../../../api/utils/lms/student/api';
import { PAGE_SIZE } from '../../../constants/constants';
import { Popover } from 'antd';
import {
  getErrorMessages,
  handleCopyClipBoard,
} from '../../../api/utils/util-func';
import {
  ArrowsRightLeftIcon,
  DocumentDuplicateIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';
import StudentTransferRequestListDialog from '../../../components/lms/student/popup/StudentTransferRequestListDialog';
import { useAppDispatch, useAppSelector } from '../../../store/hook';
import { openAlert } from '../../../store/features/modalSlice';
import { selectLmsLists } from '../../../store/features/lmsSlice';
import { LmsLinkItemKey } from '../../../constants/types/lms/type';
import LmsGuideButton from '../../../components/lms/LmsGuideButton';
import { MyClassCode } from '../../../components/lms/student/MyClassCode';

const TABS_MENU = [
  {
    label: STUDENT_STATUS_LABEL[StudentStatusEnum.ACTIVE],
    value: StudentStatusEnum.ACTIVE,
  },
  {
    label: STUDENT_STATUS_LABEL[StudentStatusEnum.INACTIVE],
    value: StudentStatusEnum.INACTIVE,
  },
  {
    label: STUDENT_STATUS_LABEL[StudentStatusEnum.PENDING],
    value: StudentStatusEnum.PENDING,
  },
];

/** 학생 클래스 등록 안내 FAQ */
const REGISTER_GUIDE_FAQ: {
  question: string;
  answer: string;
  withClassCode?: boolean;
}[] = [
  {
    question: '기존 계정이 있는 학생은 내 클래스에 어떻게 등록하나요?',
    answer:
      '기존 젭스 학생 계정이 있다면, 학생 계정에서 선생님의 클래스 코드를 입력해 등록할 수 있습니다. 아래 클래스 코드를 학생에게 알려주세요.',
    /** 답변 아래에 복사 가능한 클래스 코드를 함께 노출한다. */
    withClassCode: true,
  },
  {
    question: '담당 선생님이 변경되어 학생을 이관해야 하나요?',
    answer:
      '이미 해당 클래스를 수강한 학생을 다른 선생님의 동일한 클래스로 이관하려면 수강생 목록의 ‘이관 신청’ 버튼으로 신청해주세요. 관리자 확인 후 처리되며, 이관 시 기존 학습 기록도 함께 이전됩니다.',
  },
];

/** LMS > 학생 관리 */
const StudentMgmt = () => {
  const { classId } = useParams();

  const dispatch = useAppDispatch();

  /** 현재 보고 있는 클래스 정보. 이관 신청은 이 코스 기준으로 접수된다. */
  const currentCourseId = useAppSelector(selectLmsLists)?.course?.id;
  const currentCourseName = useAppSelector(selectLmsLists)?.course?.name;

  // --- SearchParams ---
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab =
    (searchParams.get('status') as StudentStatusEnum) ||
    StudentStatusEnum.PENDING;
  const page = searchParams.get('page') ? +searchParams.get('page') : 1;
  const pageSize = searchParams.get('pageSize')
    ? +searchParams.get('pageSize')
    : PAGE_SIZE;

  const [registerStudentDialogOpen, setRegisterStudentDialogOpen] =
    useState(false);
  const [isTransferListOpen, setIsTransferListOpen] = useState(false);
  const [transferRequests, setTransferRequests] = useState<
    StudentTransferResult[]
  >([]);
  const [isTransferLoading, setIsTransferLoading] = useState(false);

  /** 열려 있는 안내 FAQ 인덱스. 한 번에 하나만 펼친다. */
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const [studentList, setStudentList] = useState<studentType[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [pageInfo, setPageInfo] = useState<PaginationProps>({
    page,
    pageSize,
    lastPage: 0,
    total: 0,
  });

  useEffect(() => {
    reloadStudentList();
  }, [activeTab, page, pageSize]);

  /**
   * 이관 신청은 클래스가 아니라 신청자(교사) 기준으로 내려오므로 한 번만 불러오고,
   * 화면에 쓸 때 현재 코스 기준으로 걸러 쓴다.
   */
  const reloadTransferRequests = useCallback(() => {
    setIsTransferLoading(true);

    getStudentTransferRequestList()
      .then((list) => {
        setTransferRequests(list);
      })
      .catch((e) => {
        console.error(getErrorMessages(e));
      })
      .finally(() => {
        setIsTransferLoading(false);
      });
  }, []);

  useEffect(() => {
    reloadTransferRequests();
  }, [reloadTransferRequests]);

  /**
   * 지금 보고 있는 클래스(코스)의 이관 신청.
   * 목록은 신청자(교사)의 모든 신청이 내려오므로 화면에서 코스 기준으로 걸러 쓴다.
   * 코스 정보를 아직 못 받았으면 다른 코스 건이 섞이지 않도록 비워 둔다.
   */
  const courseTransferRequests = useMemo(
    () =>
      currentCourseId
        ? transferRequests.filter(
            (request) => request.courseId === currentCourseId,
          )
        : [],
    [transferRequests, currentCourseId],
  );

  /** 처리 대기 중인 신청이 있는 학생. 같은 학생을 중복 신청하지 않도록 표에서 구분한다. */
  const pendingTransferStudentIds = useMemo(
    () =>
      new Set(
        courseTransferRequests
          .filter((request) => request.status === 'PENDING')
          .map((request) => request.studentId),
      ),
    [courseTransferRequests],
  );

  const handleCopyClipboard = (text: string) => {
    handleCopyClipBoard(text).then((result) => {
      if (result) {
        dispatch(openAlert({ message: '클래스 코드가 복사되었습니다.' }));
      } else {
        dispatch(openAlert({ message: '클래스 코드 복사에 실패했습니다.' }));
      }
    });
  };

  // 페이지 정보 변경에 따른 학생 리스트 가져오기
  const reloadStudentList = () => {
    setIsLoading(true);

    getStudentList(classId, activeTab, page, pageSize)
      .then((res) => {
        if (page > 1 && res.result.length === 0) {
          searchParams.set('page', res.page.lastPage.toString());
          setSearchParams(searchParams);
          return;
        }

        setStudentList(res.result);
        setPageInfo({
          page,
          pageSize,
          total: res.page.total,
          lastPage: res.page.lastPage,
        });
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <div className="flex flex-col pt-6 xl:px-5">
      {/* 헤더 */}
      <header className="flex flex-col gap-3 px-5 xl:flex-row xl:items-start xl:justify-between xl:px-0">
        <div className="min-w-0">
          <TextWinds type="title_h2" xlType="title_h3">
            학생 관리
          </TextWinds>
          <TextWinds type="content_body3">
            학생을 새로 등록하고, 등록된 학생의 정보를 관리할 수 있습니다.
          </TextWinds>

          <div className="rounded bg-purple0 p-3">
            <TextWinds type="content_body3" color="purple5" className="mb-1">
              학생 클래스 등록 안내
            </TextWinds>
            <ul className="space-y-1 text-sm font-normal text-purple5">
              {REGISTER_GUIDE_FAQ.map((faq, index) => {
                const isOpen = openFaqIndex === index;

                return (
                  <li key={faq.question}>
                    <Popover
                      content={
                        <div className="max-w-[280px] xl:max-w-[360px]">
                          <p className="text-sm font-normal leading-relaxed text-gray8">
                            <span className="shrink-0">❓</span>
                            <span className="min-w-0 font-bold">
                              {faq.question}
                            </span>
                          </p>
                          <p className="text-sm font-normal leading-relaxed text-gray8">
                            {faq.answer}
                          </p>

                          {faq.withClassCode && (
                            <button
                              type="button"
                              className="group mt-2 flex w-full items-center justify-between gap-3 rounded-lg bg-gray0 px-3 py-2 text-left transition-colors hover:bg-blue-50"
                              onClick={() => {
                                // 복사 얼럿이 팝오버에 가려지지 않도록 먼저 닫는다.
                                setOpenFaqIndex(null);
                                handleCopyClipboard(classId);
                              }}
                            >
                              <span className="flex min-w-0 flex-col">
                                <TextWinds type="caption1" color="gray7">
                                  나의 클래스 코드
                                </TextWinds>

                                <TextWinds
                                  type="content_body3"
                                  color="gray9"
                                  className="truncate font-bold tracking-wide group-hover:text-skyblue"
                                >
                                  {classId}
                                </TextWinds>
                              </span>

                              <span className="flex shrink-0 items-center gap-1 text-gray7 group-hover:text-skyblue">
                                <DocumentDuplicateIcon className="h-4 w-4" />

                                <TextWinds
                                  type="caption1"
                                  className="font-bold group-hover:text-skyblue"
                                >
                                  복사
                                </TextWinds>
                              </span>
                            </button>
                          )}
                        </div>
                      }
                      placement="bottomLeft"
                      trigger="click"
                      /** 얼럿 모달(z-10002)보다 아래에 깔리도록 한 단계 낮춘다. */
                      zIndex={10001}
                      open={isOpen}
                      onOpenChange={(open) =>
                        setOpenFaqIndex(open ? index : null)
                      }
                    >
                      <button
                        type="button"
                        className="flex items-start gap-1 text-left"
                        aria-expanded={isOpen}
                      >
                        <span className="shrink-0">❓</span>
                        <span className="min-w-0 underline decoration-dotted underline-offset-2">
                          {faq.question} (클릭)
                        </span>
                      </button>
                    </Popover>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        <LmsGuideButton itemKey={LmsLinkItemKey.STUDENT_MANAGE} />
      </header>

      <div className="my-5 flex flex-col gap-x-1 bg-white p-4 xl:rounded-lg xl:p-4 xl:pt-6 xl:shadow-card">
        <section className="flex items-center justify-center gap-3 border-b border-gray5 pb-5 xl:justify-end">
          {/* div : 버튼 너비 조정 래퍼 */}
          <div className="w-40">
            <HFbutton
              type="skyblue-outlined"
              height={40}
              onClick={() => setIsTransferListOpen(true)}
            >
              <span className="flex items-center justify-center gap-1.5 whitespace-nowrap text-sm">
                <ArrowsRightLeftIcon className="h-5 w-5 shrink-0" />
                이관 신청 내역
                {courseTransferRequests.length > 0 &&
                  ` (${courseTransferRequests.length}건)`}
              </span>
            </HFbutton>
          </div>
          <div className="w-32">
            <HFbutton
              type="primary-filled"
              height={40}
              onClick={() => setRegisterStudentDialogOpen(true)}
            >
              <span className="flex items-center justify-center gap-1.5 whitespace-nowrap text-sm">
                <UserPlusIcon className="h-5 w-5 shrink-0" />
                신규 학생 등록
              </span>
            </HFbutton>
          </div>
        </section>

        {/* 탭 */}
        <Tabs value={activeTab} className="w-full">
          <TabsHeader
            className="rounded-none border-b border-gray6 bg-transparent p-0"
            indicatorProps={{
              className:
                'bg-transparent border-b-0 border-purple5 shadow-none rounded-none',
            }}
          >
            {TABS_MENU.map(({ label, value }) => (
              <Tab
                key={value}
                value={value}
                onClick={() => setSearchParams({ status: value })}
                className={`h-[48px] w-[142px] border-b-${
                  activeTab === value ? '2' : '0'
                } border-purple5`}
              >
                <div className="flex">
                  <TextWinds
                    type={`${
                      activeTab === value ? 'button2' : 'content_body3'
                    }`}
                    color={`${activeTab === value ? 'purple5' : 'gray7'}`}
                    xlType={`${
                      activeTab === value ? 'button' : 'content_body2'
                    }`}
                  >
                    {label}
                  </TextWinds>
                </div>
              </Tab>
            ))}
          </TabsHeader>
        </Tabs>

        {/* 중앙 테이블  */}
        <StudentList
          status={activeTab}
          data={studentList}
          pageInfo={pageInfo}
          reloadStudentList={reloadStudentList}
          isLoading={isLoading}
          pendingTransferStudentIds={pendingTransferStudentIds}
          reloadTransferRequests={reloadTransferRequests}
        />

        {/* 학생 등록 */}
        <RegisterStudentDialog
          open={registerStudentDialogOpen}
          onClose={() => setRegisterStudentDialogOpen(false)}
          reloadStudentList={reloadStudentList}
        />

        {/* 이관 신청 내역 */}
        <StudentTransferRequestListDialog
          open={isTransferListOpen}
          onClose={() => setIsTransferListOpen(false)}
          requests={courseTransferRequests}
          isLoading={isTransferLoading}
          courseName={currentCourseName}
          reloadTransferRequests={reloadTransferRequests}
        />
      </div>
    </div>
  );
};

export default StudentMgmt;
